import React, { useState, useEffect } from 'react';
import { AlertCircle, Loader2, Search, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { roleOverrideService } from '@/services/roleOverrideService';
import type { EmployeeRoleOverride } from '@/types/roleOverride';
import { formatCurrency, formatPercentage } from '@/utils/formatters';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

interface EmployeeRoleOverridesTabProps {
  employeeId?: number;
  projectId?: number;
}

export const EmployeeRoleOverridesTab: React.FC<EmployeeRoleOverridesTabProps> = ({
  employeeId,
  projectId,
}) => {
  const [overrides, setOverrides] = useState<EmployeeRoleOverride[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);

  const loadOverrides = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const data = await roleOverrideService.getEmployeeRoleOverrides(employeeId, projectId);
      setOverrides(data);
    } catch (err) {
      console.error('Error loading employee role overrides:', err);
      setError('Failed to load employee role overrides');
      toast.error('Failed to load overrides');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadOverrides();
  }, [employeeId, projectId]);

  const filteredOverrides = overrides
    .filter((override) => {
      // Only show if there's an actual override (employee has custom rates set)
      const hasCostOverride = override.employeeCostRate !== null && override.employeeCostRate > 0;
      const hasBillOverride = override.employeeBillRate !== null && override.employeeBillRate > 0;
      
      return hasCostOverride || hasBillOverride;
    })
    .filter(
      (override) =>
        override.employeeName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        override.roleName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (override.projectName && override.projectName.toLowerCase().includes(searchQuery.toLowerCase()))
    );

  // Pagination calculations
  const totalItems = filteredOverrides.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedOverrides = filteredOverrides.slice(startIndex, endIndex);

  // Reset to page 1 when search changes
  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setCurrentPage(1);
  };

  const handleItemsPerPageChange = (value: string) => {
    setItemsPerPage(Number(value));
    setCurrentPage(1);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'Active':
        return <Badge variant="default">Active</Badge>;
      case 'Expired':
        return <Badge variant="secondary">Expired</Badge>;
      case 'Future':
        return <Badge variant="outline">Future</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const calculateMargin = (costRate: number, billRate: number) => {
    if (billRate === 0) return 0;
    return ((billRate - costRate) / billRate) * 100;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search employees, roles, or projects..."
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="pl-9"
          />
        </div>
      </div>

      <div className="rounded-md border">
        <Table className="text-sm">
          <TableHeader>
            <TableRow>
              <TableHead className="h-10 p-2">Employee</TableHead>
              <TableHead className="h-10 p-2">Role</TableHead>
              <TableHead className="h-10 p-2">Project</TableHead>
              <TableHead className="h-10 p-2 text-right">Cost Rate (Hour)</TableHead>
              <TableHead className="h-10 p-2 text-right">Cost Rate (Day)</TableHead>
              <TableHead className="h-10 p-2 text-right">Bill Rate (Hour)</TableHead>
              <TableHead className="h-10 p-2 text-right">Bill Rate (Day)</TableHead>
              <TableHead className="h-10 p-2 text-right">Margin</TableHead>
              <TableHead className="h-10 p-2">Effective From</TableHead>
              <TableHead className="h-10 p-2">Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paginatedOverrides.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} className="h-24 text-center">
                  <div className="flex flex-col items-center justify-center text-muted-foreground">
                    <p className="font-medium">No employee overrides found</p>
                    <p className="text-muted-foreground">
                      Employee-specific rates will appear here when set in the Project Management app
                    </p>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              paginatedOverrides.map((override) => {
                // Employee rates (what this specific employee costs/bills)
                const employeeCostRate = override.employeeCostRate || 0;
                const employeeBillRate = override.employeeBillRate || 0;
                
                const margin = calculateMargin(employeeCostRate, employeeBillRate);

                return (
                  <TableRow key={override.id}>
                    <TableCell className="p-2">
                      <div className="font-medium">{override.employeeName}</div>
                    </TableCell>
                    <TableCell className="p-2">
                      <div className="font-medium">{override.roleName}</div>
                    </TableCell>
                    <TableCell className="p-2">
                      <div className="font-medium">{override.projectName || '—'}</div>
                    </TableCell>
                    {/* Employee Cost Rates */}
                    <TableCell className="p-2 text-right">
                      <span className="font-medium">£{employeeCostRate.toFixed(2)}</span>
                    </TableCell>
                    <TableCell className="p-2 text-right">
                      <span className="font-medium">£{(employeeCostRate * 8).toFixed(2)}</span>
                    </TableCell>
                    {/* Employee Bill Rates */}
                    <TableCell className="p-2 text-right">
                      <span className="font-medium text-green-600">£{employeeBillRate.toFixed(2)}</span>
                    </TableCell>
                    <TableCell className="p-2 text-right">
                      <span className="font-medium text-green-600">£{(employeeBillRate * 8).toFixed(2)}</span>
                    </TableCell>
                    <TableCell className="p-2 text-right">
                      <span className="font-medium text-blue-600">{margin.toFixed(0)}%</span>
                    </TableCell>
                    <TableCell className="p-2">
                      <span className="text-muted-foreground">
                        {new Date(override.effectiveFrom).toLocaleDateString()}
                      </span>
                    </TableCell>
                    <TableCell className="p-2">{getStatusBadge(override.status)}</TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {totalItems > 0 && (
        <div className="flex items-center justify-between border-t py-3 text-sm text-muted-foreground">
          <div className="flex items-center gap-2">
            <span>
              Showing {startIndex + 1} to {Math.min(endIndex, totalItems)} of {totalItems} results
            </span>
            <span>•</span>
            <div className="flex items-center gap-2">
              <span>Per page:</span>
              <Select value={itemsPerPage.toString()} onValueChange={handleItemsPerPageChange}>
                <SelectTrigger className="h-8 w-auto min-w-[60px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="25">25</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
            >
              <ChevronsLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="font-medium">
              Page {currentPage} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
            >
              <ChevronsRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
